import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
const sizes = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
};
/**
 * Modal primitive — bulletproof against tall content.
 *
 * Critical positioning is set via inline `style` props (not Tailwind
 * arbitrary classes) because Tailwind v4 (CSS-first) does not reliably
 * emit utilities for `max-h-[calc(100vh-2rem)]` and `top-[1rem]` and
 * similar — the JIT skipped these in our setup, causing the modal to
 * grow to content height and clip the title bar behind the page header.
 *
 * Layout:
 *   - Backdrop:  position: fixed, full viewport, click to close
 *   - Card:      anchored to the TOP of the viewport (not centered).
 *                Centering tall modals just hides the top half — anchoring
 *                to the top guarantees the title bar + close button are
 *                always reachable.
 *   - Header:    flex-shrink: 0, always visible
 *   - Body:      flex: 1, overflow-y: auto, scrolls internally
 *   - Footer (Cancel/Create) lives INSIDE `children` — it scrolls with
 *     the body, which is acceptable because reaching it requires
 *     completing the form anyway. (A sticky-footer variant is a future
 *     follow-on if you want Cancel/Create always-visible.)
 */
export function Modal({ open, onClose, title, children, size = 'md' }) {
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);
    if (!open)
        return null;
    // CRITICAL: render through React Portal to document.body.
    //
    // Without the portal, the modal lives wherever its caller is in the
    // React tree — typically inside <main>/<Outlet>/.animate-fade-in.
    // The .animate-fade-in class (see styles.css) applies a CSS
    // `transform` property which (per the CSS spec) creates a NEW
    // containing block for `position: fixed` descendants. The modal's
    // `position: fixed; top: 0; left: 0; ...` then becomes relative to
    // that ancestor instead of the viewport — so on large viewports the
    // title bar ends up clipped behind the page header strip, while on
    // small viewports a coincidence of math makes it visible again.
    //
    // Portaling to document.body sidesteps the entire issue. The modal
    // is rendered as a direct child of <body>, so no ancestor's
    // transform / filter / perspective / will-change / contain can ever
    // re-scope its fixed positioning. This is the standard pattern in
    // every major React UI library (Radix, Headless UI, MUI, Chakra,
    // Mantine, Ant Design).
    return createPortal(_jsx("div", { 
        // Backdrop — covers the whole viewport. Anchored top so tall modals
        // don't shift their title bars out of view.
        //
        // Click-outside-to-close was REMOVED per industry standard for
        // FORM modals (Salesforce, ServiceNow, Dynamics, Zendesk,
        // Freshdesk). Form data loss to a misclick on the backdrop is a
        // real UX cost; users dismiss via the explicit ✕ button or
        // Cancel action inside the form, or with the Escape key (which
        // remains active via the useEffect above for accessibility).
        style: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 50,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '2rem 1rem 1rem',
            background: 'rgba(0,0,0,0.4)',
        }, children: _jsxs("div", { className: cn('w-full rounded-lg bg-white shadow-xl dark:bg-slate-900', sizes[size]), 
            // Card — capped at viewport height minus the wrapper's top+bottom
            // padding (2rem top + 1rem bottom = 3rem). Flex column so header
            // stays fixed and body can scroll.
            style: {
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 3rem)',
            }, children: [_jsxs("div", { 
                    // Distinct tinted background so the header is visually
                    // unmistakable as a separate region. Explicit text colors so
                    // Tailwind v4's preflight (which clears default heading styles
                    // including color inheritance edge-cases) can't silently make
                    // the title invisible on certain backgrounds.
                    className: "flex items-center justify-between rounded-t-lg border-b border-slate-200 bg-slate-50 px-5 py-3 dark:border-slate-700 dark:bg-slate-800/60", style: { flexShrink: 0 }, children: [_jsx("h2", { className: "text-lg font-semibold text-slate-900 dark:text-slate-100", 
                            // Inline fallback in the rare case a global CSS reset wins
                            // over Tailwind utilities.
                            style: { margin: 0, color: 'inherit' }, children: title }), _jsx("button", { onClick: onClose, className: "rounded-md p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100", "aria-label": "Close", type: "button", children: "\u2715" })] }), _jsx("div", { className: "p-5", style: { flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }, children: children })] }) }), document.body);
}
